<template>
	<div>
		<UForm
			:id="SIGNUP_FORM"
			class="grid w-full grid-cols-1 text-left gap-y-2"
			:state="state"
			:schema="schema"
			@error="onError"
		>
			<fieldset class="px-4">
				<legend class="w-full text-lg font-semibold text-left pt-6 pb-2">
					{{ t('get-fiber.form.street-address') }}
				</legend>
				<div class="flex items-center gap-2 text-sm pb-8">
					<UIcon
						name="i-lucide-map-pin"
						class="h-8 w-8 shrink-0 text-primary"
					/>
					<span class="font-semibold">
						{{ address }}
					</span>
					<UButton
						id="change-address-button"
						:to="localePath('/get-fiber')"
						:ui="{
							label: 'text-sm font-normal'
						}"
						color="primary"
						variant="ghost"
						:label="t('get-fiber.change')"
						class="hover:underline decoration-primary decoration-2 underline-offset-2"
						@click="handleChangeAddress"
					/>
				</div>

				<div class="flex flex-col w-full">
					<UForm
						v-if="state.movingIn"
						:state="state"
						:schema="nestedSchema"
						attach
					>
						<div class="pb-4">
							<h2 class="pb-0 mb-0 text-lg font-semibold text-left">
								{{ t('get-fiber.form.moving-in-date.title') }}
							</h2>
							<p class="text-sm text-dimmed">
								{{ t('get-fiber.form.moving-in-date.subtitle') }}
							</p>
						</div>
						<UFormField
							name="moveInDate"
							:label="t('get-fiber.form.moving-in-date.label')"
							:required="state.movingIn"
							class="pb-6 md:pr-6"
						>
							<UInput
								:id="t('get-fiber.form.moving-in-date.id')"
								v-model="state.moveInDate"
								:label="t('get-fiber.form.moving-in-date.label')"
								:placeholder="t('get-fiber.form.moving-in-date.label')"
								:autocomplete="MOVE_IN_DATE_AUTOCOMPLETE"
								:type="DATE"
								class="col-span-1 w-full lg:w-1/2"
								@focus="handleFocus('moveInDate')"
								@blur="handleBlur('moveInDate')"
								@input="handleInput('moveInDate', { move_in_date: state.moveInDate })"
							/>
						</UFormField>
					</UForm>
					<div class="grid w-full grid-cols-1 pb-4 md:grid-cols-2 md:gap-x-6 gap-y-4">
						<UFormField
							name="firstName"
							:label="t('get-fiber.form.first-name.label')"
							required
						>
							<UInput
								:id="t('get-fiber.form.first-name.id')"
								v-model="state.firstName"
								:type="TEXT"
								:label="t('get-fiber.form.first-name.label')"
								:placeholder="t('get-fiber.form.first-name.label')"
								:autocomplete="FIRST_NAME_AUTOCOMPLETE"
								class="w-full"
								@focus="handleFocus('firstName')"
								@blur="handleBlur('firstName')"
								@input="handleInput('firstName')"
							/>
						</UFormField>
						<UFormField
							name="lastName"
							:label="t('get-fiber.form.last-name.label')"
							required
						>
							<UInput
								:id="t('get-fiber.form.last-name.id')"
								v-model="state.lastName"
								:type="TEXT"
								:label="t('get-fiber.form.last-name.label')"
								:placeholder="t('get-fiber.form.last-name.label')"
								:autocomplete="LAST_NAME_AUTOCOMPLETE"
								class="w-full"
								@focus="handleFocus('lastName')"
								@blur="handleBlur('lastName')"
								@input="handleInput('lastName')"
							/>
						</UFormField>

						<UFormField
							name="email"
							:label="t('get-fiber.form.email.label')"
							required
						>
							<UInput
								:id="t('get-fiber.form.email.id')"
								v-model="state.email"
								:type="EMAIL"
								:label="t('get-fiber.form.email.label')"
								:placeholder="t('get-fiber.form.email.label')"
								:autocomplete="EMAIL_AUTOCOMPLETE"
								class="w-full"
								@focus="handleFocus('email')"
								@blur="handleBlur('email')"
								@input="handleInput('email')"
							/>
						</UFormField>

						<UFormField
							name="phone"
							:label="t('get-fiber.form.phone-number.label')"
							required
						>
							<FormPhoneInput
								:id="t('get-fiber.form.phone-number.id')"
								v-model="state.phone"
								@update:model-value="state.phone = $event"
								@focus="handleFocus('phone')"
								@blur="handleBlur('phone')"
							/>
						</UFormField>
					</div>
					<div class="pb-2">
						<div class="w-full flex items-center text-sm font-semibold">
							<UCheckbox
								v-model="state.isMobilePhone"
								name="isMobilePhone"
								:default-value="true"
								:label="t('get-fiber.form.phone-type.label')"
								size="sm"
								color="primary"
								:ui="{
									label: 'text-sm font-semibold'
								}"
								@change="handleInput('isMobilePhone', { phone_type: state.isMobilePhone ? 'mobile' : 'home' })"
							/>
						</div>
					</div>
					<div class="flex flex-col w-full justify-start gap-1">
						<div class="flex items-center mt-4 text-sm font-semibold">
							<UCheckbox
								v-model="state.optIn"
								name="optIn"
								:default-value="true"
								:label="t('get-fiber.form.terms-and-conditions.title')"
								size="sm"
								color="primary"
								aria-describedby="terms-and-conditions-description"
								:ui="{
									label: 'text-sm font-semibold'
								}"
								@change="handleInput('optIn', { opt_in: state.optIn ? 'accepted' : 'declined' })"
							/>
						</div>
						<p
							id="terms-and-conditions-description"
							class="pb-4 text-xs"
							v-html="t('get-fiber.form.terms-and-conditions.text')"
						/>
					</div>
				</div>
			</fieldset>
			<fieldset
				v-if="showAccountCreation"
				class="px-4"
			>
				<legend class="py-2 text-lg font-semibold">
					{{ t('get-fiber.form.account-creation') }}
				</legend>
				<p class="pb-4 text-sm font-normal">
					{{ t('get-fiber.form.password-instructions') }}
				</p>
				<UForm
					v-if="showAccountCreation"
					:state="state"
					:schema="nestedSchema"
					attach
				>
					<div class="grid grid-cols-1 pb-4 md:pb-0 gap-y-6 gap-x-6 md:grid-cols-2">
						<div class="space-y-2">
							<UFormField
								name="password"
								:label="t('get-fiber.form.password.label')"
								:required="showAccountCreation"
							>
								<UInput
									v-model="state.password"
									:placeholder="t('get-fiber.form.password.label')"
									:color="color"
									:type="show ? 'text' : 'password'"
									:ui="{ trailing: 'pe-1' }"
									:aria-invalid="score < 5"
									aria-describedby="password-strength"
									class="w-full"
									@focus="handleFocus('password')"
									@blur="handleBlur('password')"
									@input="handleInput('password')"
								>
									<template #trailing>
										<UButton
											color="neutral"
											variant="link"
											size="sm"
											:icon="show ? 'i-lucide-eye-off' : 'i-lucide-eye'"
											:aria-label="show ? t('get-fiber.form.password.button.hide') : t('get-fiber.form.password.button.show')"
											:aria-pressed="show"
											aria-controls="password"
											@click="show = !show"
										/>
									</template>
								</UInput>
							</UFormField>

							<UProgress
								:color="color"
								:indicator="text"
								:model-value="score"
								:max="5"
								size="sm"
							/>

							<p
								id="password-strength"
								class="text-sm font-medium"
							>
								{{ text }}. {{ t('get-fiber.form.password.validation.requirements.must-contain') }}
							</p>

							<ul
								class="space-y-1"
								:aria-label="t('get-fiber.form.password.validation.requirements.aria-label')"
							>
								<li
									v-for="(req, index) in strength"
									:key="index"
									class="flex items-center gap-0.5"
									:class="req.met ? 'text-success' : 'text-muted'"
								>
									<UIcon
										:name="req.met ? 'i-lucide-circle-check' : 'i-lucide-circle-x'"
										class="size-4 shrink-0"
									/>

									<span class="text-xs font-light">
										{{ req.text }}
										<span class="sr-only">
											{{ req.met ? t('get-fiber.form.password.validation.requirements.met') : t('get-fiber.form.password.validation.requirements.not-met') }}
										</span>
									</span>
								</li>
							</ul>
						</div>

						<UFormField
							name="passwordConfirm"
							:label="t('get-fiber.form.password-confirm.label')"
							:help="confirmText"
							:required="showAccountCreation"
						>
							<div class="space-y-2">
								<UInput
									:id="t('get-fiber.form.password-confirm.id')"
									v-model="state.passwordConfirm"
									:label="t('get-fiber.form.password-confirm.label')"
									:placeholder="t('get-fiber.form.password-confirm.label')"
									:autocomplete="NEW_PASSWORD_AUTOCOMPLETE"
									:color="confirmColor"
									:type="show ? 'text' : 'password'"
									class="w-full"
									@focus="handleFocus('passwordConfirm')"
									@blur="handleBlur('passwordConfirm')"
									@input="handleInput('passwordConfirm')"
								>
									<template #trailing>
										<UButton
											color="neutral"
											variant="link"
											size="sm"
											:icon="show ? 'i-lucide-eye-off' : 'i-lucide-eye'"
											:aria-label="show ? t('get-fiber.form.password.button.hide') : t('get-fiber.form.password.button.show')"
											:aria-pressed="show"
											aria-controls="password"
											@click="show = !show"
										/>
									</template>
								</UInput>

								<UProgress
									:color="confirmProgressColor"
									:indicator="confirmText"
									:model-value="confirmScore"
									:max="2"
									size="sm"
								/>
							</div>
						</UFormField>
					</div>
				</UForm>
			</fieldset>
			<div class="flex justify-center px-4 pb-12 pt-4 sm:pt-12 sm:pb-20 text-sm">
				<UButton
					type="submit"
					size="xl"
					:loading="loading"
					:label="t('get-fiber.form.button-text')"
					:ui="{
						label: 'uppercase font-bold',
						base: 'w-full max-w-[16rem]'
					}"
					@click="handleFormSubmit"
				/>
			</div>
		</UForm>
	</div>
</template>

<script setup lang="ts">
import { LazyModalDuplicateAccount } from '#components'
import { useErrorReporter } from '@/composables/useErrorReporter'
import { useGA4Tracking } from '@/composables/useGA4Tracking'
import { useAddressLocalStorage } from '@/composables/useLocalStorage'
import { useZod } from '@/composables/useZod'
import {
	EMAIL_AUTOCOMPLETE,
	FIRST_NAME_AUTOCOMPLETE,
	LAST_NAME_AUTOCOMPLETE,
	MOVE_IN_DATE_AUTOCOMPLETE,
	NEW_PASSWORD_AUTOCOMPLETE
} from '@/constants/form/autocompletes.js'
import {
	DATE,
	EMAIL,
	TEXT
} from '@/constants/form/text-inputs/types.js'
import {
	API_BASE, API_ENDPOINT_SIGNUP
} from '@/constants/index.js'
import {
	AVAILABLE, COMPLETE, UNAVAILABLE, UNDER_CONSTRUCTION
} from '@/constants/signup-queries'
import type {
	MappedPackage, Zone
} from '@/types/components/signup'
import { mapStatusToZone } from '@/utils/fiberStatusMap'
import { trimPhone } from '@/utils/formatPhone'
import type {
	FormErrorEvent
	// FormSubmitEvent
} from '@nuxt/ui'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

const SIGNUP_FORM = 'signup-form'

const {
	signupFormState: state,
	signupFormSchema: schema,
	signupFormNestedSchema: nestedSchema
} = useZod()

const { t } = useI18n({
	useScope: 'global'
})
const localePath = useLocalePath()

const route = useRoute()
const toast = useToast()

const { cachedAddressResponse } = useAddressLocalStorage()

const { bugsnagReport } = useErrorReporter()

const {
	trackInputEvent,
	trackButtonClick,
	trackFormSubmit,
	trackServicePlan,
	trackError
} = useGA4Tracking()

const props = defineProps({
	packageLevel: {
		type: Number,
		default: 0
	},
	movingIn: {
		type: Boolean,
		default: false
	},
	billables: {
		type: Array as PropType<MappedPackage[]>,
		required: true
	}
})

const {
	packageLevel,
	movingIn,
	billables
} = toRefs(props)

const loading = ref(false)

// Watch for changes in movingIn and update state accordingly
// to ensure zod validation works correctly
watch(movingIn, (newValue: boolean) => {
	state.movingIn = newValue
}, { immediate: true })

// Event tracking handlers
const handleFocus = (fieldId: string) => {
	trackInputEvent('focus', fieldId, SIGNUP_FORM)
}

const handleBlur = (fieldId: string) => {
	trackInputEvent('blur', fieldId, SIGNUP_FORM)
}

const handleInput = (fieldId: string, parameters?: Record<string, any>) => {
	trackInputEvent('input', fieldId, SIGNUP_FORM, parameters)
}

const overlay = useOverlay()

const duplicateAccountModal = overlay.create(LazyModalDuplicateAccount, {})

const show = ref(false)

function checkStrength(str: string) {
	const requirements = [
		{
			regex: /.{12,}/,
			text: t('get-fiber.form.password.validation.requirements.min-length')
		},
		{
			regex: /\d/,
			text: t('get-fiber.form.password.validation.requirements.min-numbers')
		},
		{
			regex: /[a-z]/,
			text: t('get-fiber.form.password.validation.requirements.min-lowercase')
		},
		{
			regex: /[A-Z]/,
			text: t('get-fiber.form.password.validation.requirements.min-uppercase')
		},
		{
			regex: /[!@#$%^&*(),.?":{}|<>]/,
			text: t('get-fiber.form.password.validation.requirements.min-special')
		}
	]

	return requirements.map(req => ({
		met: req.regex.test(str),
		text: req.text
	}))
}

const strength = computed(() => checkStrength(state?.password || ''))
const score = computed(() => strength.value.filter((req: {
	text: string
	met: boolean
}) => req.met).length)

const color = computed(() => {
	if (score.value === 0) return 'info'
	if (score.value <= 1) return 'error'
	if (score.value <= 3) return 'warning'
	if (score.value === 4) return 'info'
	return 'success'
})

const text = computed(() => {
	if (score.value === 0) return t('get-fiber.form.password.validation.score-none')
	if (score.value <= 1) return t('get-fiber.form.password.validation.score-weak')
	if (score.value === 4) return t('get-fiber.form.password.validation.score-medium')
	return t('get-fiber.form.password.validation.score-strong')
})

const confirmScore = computed(() => {
	if (state.passwordConfirm && state.passwordConfirm.length > 0) {
		return state.password === state.passwordConfirm ? 2 : 1
	}
	return 0
})

const confirmColor = computed(() => {
	if (confirmScore.value === 0) return 'info'
	if (confirmScore.value === 1) return 'error'
	return 'success'
})

const confirmProgressColor = computed(() => {
	if (state.passwordConfirm && state.passwordConfirm.length > 0) {
		return state.password === state.passwordConfirm ? 'success' : 'error'
	}
	return undefined
})

const confirmText = computed(() => {
	if (state.passwordConfirm && state.passwordConfirm.length > 0) {
		return state.password === state.passwordConfirm
			? t('get-fiber.form.password-confirm.validation.pattern-match')
			: t('get-fiber.form.password-confirm.validation.pattern-failed')
	}
	return t('get-fiber.form.password-confirm.validation.required')
})

async function onError(event: FormErrorEvent) {
	if (event?.errors?.[0]?.id) {
		const element = document.getElementById(event.errors[0].id)
		element?.focus()
		element?.scrollIntoView({
			behavior: 'smooth',
			block: 'center'
		})

		trackError('Signup Form Validation Error: ' + event.errors[0].message, SIGNUP_FORM)
	}
}

const address = computed(() => {
	return cachedAddressResponse?.value?.address || ''
})

const zoneType = computed<Zone>(() => {
	return mapStatusToZone(cachedAddressResponse.value?.status)
})

const addressId = computed(() => {
	return cachedAddressResponse.value?.addressId || 0
})

const packageLevelSelected = computed(() => {
	return packageLevel.value > 0
})

const showPackageSelection = computed(() => {
	return route.query?.fiber !== UNAVAILABLE
})

const showAccountCreation = computed(() => {
	return route.query?.fiber !== UNAVAILABLE
})

// Handle change address button click
const handleChangeAddress = () => {
	trackButtonClick('change-address', 'button', {
		address: address.value,
		form_id: SIGNUP_FORM
	})
}

const isFormValid = computed(() => {
	// Check if fiber is unavailable (no package level needed) or otherwise that a package level is selected
	const isPackageSelectionValid = route.query?.fiber === UNAVAILABLE || packageLevelSelected.value

	return isPackageSelectionValid
})

const formData = computed(() => {
	return {
		addressId: addressId.value,
		zoneType: zoneType.value,
		firstName: state.firstName?.trim(),
		lastName: state.lastName?.trim(),
		phone: trimPhone(state.phone || ''),
		email: state.email?.trim(),
		billableId: packageLevel.value,
		password: state.password?.trim(),
		passwordAgain: state.passwordConfirm?.trim(),
		phoneType: state.isMobilePhone ? 'mobile' : 'home',
		phoneOptOut: !state.optIn,
		moveInDate: movingIn.value ? state.moveInDate : ''
	}
})

const handleFormSubmit = async (/* event: FormSubmitEvent<Schema> */) => {
	if (!isFormValid.value) { // Handle other validation errors that the form schema does not catch
		if (showPackageSelection.value && !packageLevelSelected.value) {
			trackError('No package selected', SIGNUP_FORM)
			toast.add({
				title: t('get-fiber.form.error-toast.title'),
				description: t('get-fiber.form.error-toast.no-package')
			})
		} else {
			trackError('All fields are required', SIGNUP_FORM)
			toast.add({
				title: t('get-fiber.form.error-toast.title'),
				description: t('get-fiber.form.error-toast.all-fields')
			})
		}
	} else {
		// Track form submission
		trackFormSubmit(SIGNUP_FORM, 'registration', {
			// NOTE: Always convert to snake_case for GA4,
			// and dont send sensitive data like name/phone/email/street address
			zip_code: cachedAddressResponse.value?.zipCode || '',
			tier: formData.value.billableId,
			zone_type: formData.value.zoneType,
			moving_in: movingIn.value,
			move_in_date: formData.value.moveInDate,
			phone_type: formData.value.phoneType,
			phone_opt_out: formData.value.phoneOptOut
		})
		const selectedPackage = billables.value?.find((billable: MappedPackage) => billable.id === formData.value.billableId)
		if (selectedPackage) {
			trackServicePlan('purchase', formData.value.billableId, selectedPackage.packageSpeed, selectedPackage.price)
		}

		loading.value = true

		const hasLoginErrorMessage = (message: string) => {
			return message?.includes('duplicate signup - has login')
				|| message?.includes('It looks like you already have a login with this email address & location, please attempt to log in. If you are unable to log in, you may need to reset your password.')
		}

		await $fetch(`${API_BASE}${API_ENDPOINT_SIGNUP}`, {
			method: 'POST',
			body: formData.value,
			onRequestError({ error }) {
				const errorMessage = error?.message || 'Error submitting signup form'
				bugsnagReport({
					error: new Error(errorMessage),
					showToast: true,
					toast: {
						title: 'Oops! Something went wrong',
						message: errorMessage
					}
				})
				loading.value = false
			},
			onResponse({ response }) {
				if (!response?.ok) {
					throw new Error(response._data?.error || response._data?.errors?.[0]?.title)
				}
				if (response.status === 200) {
					navigateTo({
						path: localePath('/get-fiber'),
						query: { fiber: COMPLETE }
					})
				}
				loading.value = false
			}
		}).catch(e => {
			const sanitizeMessage = (errorMessage: string): string => {
				if (
					errorMessage?.includes('Email already in use, please use a different email address')
				) {
					return t('get-fiber.form.login-error.duplicate-signup')
				}
				if (errorMessage?.includes('Please submit a valid billableId')) {
					return t('get-fiber.form.login-error.no-package')
				}
				if (errorMessage?.includes('Missing Required')) {
					return t('get-fiber.form.login-error.all-fields')
				}
				return errorMessage || t('get-fiber.form.login-error.fallback')
			}
			// if zone is available or under construction and there is a duplicate account open the dupe modal.
			// if a zone is not available/under construction we need to just pop up the error returned
			if (hasLoginErrorMessage(e?.message) && [ AVAILABLE, UNDER_CONSTRUCTION ].includes(zoneType.value)) {
				duplicateAccountModal.open()
			} else {
				bugsnagReport({
					error: new Error(e),
					showToast: true,
					toast: {
						title: 'Oops! Something went wrong',
						message: sanitizeMessage(e.message)
					}
				})
			}
		}).finally(() => {
			loading.value = false
		})
	}
}
</script>
